import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  output,
  inject,
  input,
} from '@angular/core';

import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  Validators,
  UntypedFormGroup,
  AbstractControl,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';

import { ToastMessageService } from '@fitness-force/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChargeCategoryService } from '@fitness-force/api';
import { NotFoundError } from '@fitness-force/models';
import { ErrorMsgComponent } from '@fitness-force/errors';
import { Store } from '@ngrx/store';
import { COMPANY_INFO, TENANTS_INFO } from '@fitness-force/state';
import { combineLatest, Observable } from 'rxjs';

@Component({
  selector: 'charge-category-create',
  templateUrl: './charge-category-create.component.html',
  providers: [ToastMessageService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    RippleModule,
    DialogModule,
    DividerModule,
    InputTextModule,
    TranslateModule,
    ErrorMsgComponent,
  ],
})
export class ChargeCategoryCreateComponent implements OnInit {
  private store = inject(Store);
  private chargeCategoryService = inject(ChargeCategoryService);
  private toastMessageService = inject(ToastMessageService);
  private translate = inject(TranslateService);
  private fb = inject(UntypedFormBuilder);
  private changeDetectorRef = inject(ChangeDetectorRef);

  readonly activeCategory = input<any>();
  readonly changed = output();

  localActiveCategory: any = null;

  getAccessTenantList$: Observable<any> = new Observable();
  showOptions: boolean = false;
  tenantId: number = 0;

  serverCategoryForm: UntypedFormGroup;
  primaryLanguage!: string;
  secondaryLanguage!: string;
  isSecondaryLanguage = false;
  apptCatParams = new Map();

  ngOnInit(): void {
    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);

    combineLatest([
      this.getAccessTenantList$,
      this.store.select(COMPANY_INFO),
    ]).subscribe({
      next: ([tenant, company]: any) => {
        this.tenantId = tenant.selected_tenant.id;
        if (this.tenantId > 0) {
          this.store.dispatch({ type: 'ROUTER_GO' });

          if (tenant.selected_tenant?.secondary_language) {
            this.isSecondaryLanguage = true;
            this.translate
              .get([
                'common.LANGUAGE.' +
                  tenant.selected_tenant.primary_language?.toUpperCase(),
                'common.LANGUAGE.' +
                  tenant.selected_tenant.secondary_language?.toUpperCase(),
              ])
              .subscribe((translations) => {
                this.primaryLanguage =
                  translations[
                    'common.LANGUAGE.' +
                      tenant.selected_tenant.primary_language?.toUpperCase()
                  ];
                this.secondaryLanguage =
                  translations[
                    'common.LANGUAGE.' +
                      tenant.selected_tenant.secondary_language?.toUpperCase()
                  ];
              });
          }
          this.loadCategory();
        }
      },
    });
  }

  handleError(error: any) {
    if (error instanceof NotFoundError) {
      this.translate
        .get([
          'common.errors.SERVER_SIDE.ERROR_OCCURRED',
          'common.errors.SERVER_SIDE.PAGE_NOT_FOUND',
        ])
        .subscribe((translations: any) => {
          this.toastMessageService.showErrorMessage(translations);
        });
    } else if (
      error.originalError &&
      error.originalError.error &&
      error.originalError.error[0]
    ) {
      let currentError = error.originalError.error[0];
      if (currentError.errorCode == 'alreadyexist') {
        if (
          currentError?.identifier &&
          currentError.identifier == 'name_pri_lang'
        )
          this.NamePrimary?.setErrors({
            duplicate: true,
          });
        else if (
          currentError?.identifier &&
          currentError.identifier == 'name_sec_lang'
        )
          this.NameSecondary?.setErrors({
            duplicate: true,
          });
        this.changeDetectorRef.detectChanges();
      }
    }
    {
      this.translate
        .get([
          'common.errors.SERVER_SIDE.ERROR_OCCURRED',
          'common.errors.SERVER_SIDE.SOMETHING_WRONG',
        ])
        .subscribe((translations: any) => {
          this.toastMessageService.showErrorMessage(translations);
        });
    }
  }

  loadCategory() {
    const activeCategory = this.activeCategory();
    if (activeCategory) {
      this.chargeCategoryService
        .getSingleChargeCategory(this.tenantId, activeCategory.id)
        .subscribe({
          next: (res) => {
            this.localActiveCategory = res;
            this.buildEditForm();
            this.changeDetectorRef.detectChanges();
          },
          error: (error) => {
            this.handleError(error);
          },
        });
    } else {
      this.buildEditForm();
    }
  }

  get NamePrimary(): AbstractControl | null {
    return this.serverCategoryForm.get('name_primary_language');
  }
  get NameSecondary(): AbstractControl | null {
    return this.serverCategoryForm.get('name_secondary_language');
  }

  get DescriptionPrimary(): AbstractControl | null {
    return this.serverCategoryForm.get('description_primary_language');
  }

  get DescriptionSecondary(): AbstractControl | null {
    return this.serverCategoryForm.get('description_secondary_language');
  }

  buildEditForm() {
    const activeCategory = this.activeCategory();
    const activeCategoryValue = this.activeCategory();
    const activeCategoryVal = this.activeCategory();
    const activeCategoryInput = this.activeCategory();
    const activeCategory_1 = this.activeCategory();
    this.serverCategoryForm = this.fb.group({
      name_primary_language: [
        activeCategory && activeCategory.name_primary_language
          ? activeCategory.name_primary_language
          : '',
        Validators.required,
      ],
      name_secondary_language: [
        activeCategoryValue && activeCategoryValue.name_secondary_language
          ? activeCategoryValue.name_secondary_language
          : '',
      ],
      description_primary_language: [
        activeCategoryVal && activeCategoryVal.description_primary_language
          ? activeCategoryVal.description_primary_language
          : '',
      ],
      description_secondary_language: [
        activeCategoryInput &&
        activeCategoryInput.description_secondary_language
          ? activeCategoryInput.description_secondary_language
          : '',
      ],
      icon: [
        activeCategory_1 && activeCategory_1.icon ? activeCategory_1.icon : '',
      ],
    });
  }
  get Icon(): AbstractControl | null {
    return this.serverCategoryForm.get('icon');
  }
  setIcon(icon: string) {
    this.Icon?.setValue(icon);
  }

  updateServiceCategory() {
    if (this.serverCategoryForm.valid) {
      let data = this.serverCategoryForm.getRawValue();
      const activeCategory = this.activeCategory();
      if (activeCategory) {
        this.chargeCategoryService
          .updateChargeCategory(this.tenantId, activeCategory.id, data)
          .subscribe({
            next: (res) => {
              // this.activeCategory = null;
              this.changed.emit(res);
              // this.ref.close(res);
              this.translate
                .get([
                  'ADMIN.SERVICES.SERVICE_CATEGORY_UPDATE',
                  'ADMIN.SERVICES.SERVICE_CATEGORY_UPDATE_MESSAGE',
                ])
                .subscribe((translations) => {
                  this.toastMessageService.showSuccess(translations);
                });
            },
            error: (error) => {
              this.handleError(error);
            },
          });
      } else {
        this.chargeCategoryService
          .createChargeCategory(this.tenantId, data)
          .subscribe({
            next: (res) => {
              this.localActiveCategory = null;
              this.changed.emit(res);
              this.translate
                .get([
                  'ADMIN.SERVICES.SERVICE_CATEGORY_UPDATE',
                  'ADMIN.SERVICES.SERVICE_CATEGORY_UPDATE_MESSAGE',
                ])
                .subscribe((translations) => {
                  this.toastMessageService.showSuccess(translations);
                });
            },
            error: (error) => {
              this.handleError(error);
            },
          });
      }
    } else {
      Object.keys(this.serverCategoryForm.controls).forEach((field) => {
        const control = this.serverCategoryForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
    }
  }

  categoryCancel() {
    this.changed.emit();
    // this.ref.close();
  }
}
